import {
  Component,
  HostListener,
  Input,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { User } from '../../models/User';
import { UserService } from '../../services/user.service';
import { faUser } from '@fortawesome/free-solid-svg-icons';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { Router } from '@angular/router';
import { environment } from 'src/environments/environment';
import { DeleteService } from 'src/app/features/delete/services/delete.service';
import { ToastrService } from 'ngx-toastr';
import { SearchService } from '../../services/search.service';

@Component({
  selector: 'app-list-users',
  templateUrl: './list-users.component.html',
  styleUrls: ['./list-users.component.scss'],
})
export class ListUsersComponent implements OnInit {
  @Input() showDetailsModalOnSelect!: boolean;
  @Input() navigateToUpdateOnSelect!: boolean;
  @Input() showConfirmDeleteModalOnSelect!: boolean;
  @Input() query!: string;

  users: User[] = [];
  isUserSelected: boolean = false;
  selectedUser: any;

  faUser = faUser;
  faGlobe = faGlobe;
  faPenToSquare = faPenToSquare;

  user: any;
  isAdmin: boolean = false;

  isLoading: boolean = false;
  isDeleting: boolean = false;
  isLoadingMoreData: boolean = false;

  skipToken: string = '';
  disableLoadMoreButton: boolean = false;

  numberOfUsers: number = 0;

  mail: string = '';
  isEmailEntered: boolean = false;

  constructor(
    private userService: UserService,
    private router: Router,
    private deleteService: DeleteService,
    private toastr: ToastrService,
    private searchService: SearchService
  ) {}

  ngOnInit(): void {
    this.getUsers(10);
    this.isAdmin = this.checkIfLoggedInUserIsAdmin();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      !changes['query'].firstChange &&
      changes['query'].previousValue != changes['query'].currentValue &&
      this.query.length >= 3
    ) {
      this.search();
    } else if (!changes['query'].firstChange && this.query.length == 0) {
      this.getUsers(this.numberOfUsers);
    }
  }

  getUsers(top: number) {
    this.isLoading = true;
    this.userService.getAllUsers(this.skipToken, top).subscribe(
      (data: any) => {
        if (data.nextPage != null) {
          this.skipToken = data.nextPage.split('$skiptoken=')[1];
        }
        this.users = this.cleanUsers(data.currentPage);
        this.numberOfUsers = this.users.length;
        this.isLoading = false;
      },
      (error) => {
        this.isLoading = false;
      }
    );
  }

  getCustomAttributes(user: User){
    let customAttributes = this.buildMap(user.customAttributes); 
    let investCloudRole: string;
    if(customAttributes.size == 0){
      investCloudRole = "unknown";
      return investCloudRole;
    }
    
    customAttributes.forEach((value: string, key: string) => {
      if(key.includes("RoleName"))
        investCloudRole = customAttributes.get(key);
    });
    
    if(investCloudRole! == undefined)
      return "unknown";
    else
      return investCloudRole;
  }

  selectUser(user: any) {
    if (this.showDetailsModalOnSelect) {
      this.isUserSelected = true;
      this.selectedUser = user;
      document.getElementById('openModalButton')?.click();
    } else if (this.navigateToUpdateOnSelect) {
      this.router.navigateByUrl('/update/' + user.id);
    } else if (this.showConfirmDeleteModalOnSelect) {
      this.isUserSelected = true;
      this.selectedUser = user;
      this.mail = '';
      this.isEmailEntered = false;
      document.getElementById('openConfirmationModalButton')?.click();
    }
  }

  search() {
    if (this.query.length >= 3) {
      this.isLoading = true;
      this.searchService.searchByNameAndEmail(this.query).subscribe(
        (data: any) => {
          this.users = this.cleanUsers(data);
          this.isLoading = false;
        },
        (error) => {
          this.isLoading = false;
        }
      );
    }
  }

  cleanUsers(users: any[]) {
    if (users != undefined) {
      users.forEach((user) => {
        let index = user.identities.findIndex(
          (i: { signInType: string }) => i.signInType == 'emailAddress'
        );
        if (index != -1) {
          user.mail = user.identities[index].issuerAssignedId;
        } else if (user.mail == null && user.userPrincipalName != null) {
          user.mail = user.userPrincipalName;
        } else if (user.mail == null && user.userPrincipalName == null) {
          user.mail = 'unknown';
        }
      });
    }

    return users;
  }

  checkIfLoggedInUserIsAdmin(): boolean {
    this.user = JSON.parse(localStorage.getItem('user') || '{}');
    if (this.user != null && this.user.idTokenClaims != undefined) {
      let groups: string[] = this.user.idTokenClaims['groups'] as string[];
      if (
        groups.includes(environment.adminGroupID) ||
        groups.includes(environment.superAdminGroupID)
      ) {
        return true;
      } else {
        return false;
      }
    } else {
      return false;
    }
  }

  delete() {
    this.isDeleting = true;
    this.deleteService.deleteUser(this.selectedUser.id).subscribe(
      (response) => {
        let index: number = this.users.indexOf(this.selectedUser);
        if (index !== -1) {
          this.users.splice(index, 1);
          document.getElementById('closeConfirmationModalButton')?.click();
        }
        this.isDeleting = false;
        this.toastr.success(
          'User ' + this.selectedUser.displayName + ' is successfully deleted.',
          'Success'
        );
      },
      (error) => {
        this.isDeleting = false;
        this.toastr.error(
          'User ' +
            this.selectedUser.displayName +
            ' is not successfully deleted.',
          'Failure'
        );
      }
    );
  }

  checkIfEmailEntered() {
    if (this.mail == this.selectedUser.mail) {
      this.isEmailEntered = true;
    } else {
      this.isEmailEntered = false;
    }
  }

  loadMore() {
    this.isLoadingMoreData = true;
    this.userService.getAllUsers(this.skipToken, 10).subscribe(
      (data: any) => {
        if (data.nextPage != null) {
          this.skipToken = data.nextPage.split('$skiptoken=')[1];
        } else {
          this.disableLoadMoreButton = true;
        }
        let users = this.cleanUsers(data.currentPage);
        this.users = [...this.users, ...users];
        this.numberOfUsers = this.users.length;
        this.isLoadingMoreData = false;
      },
      (error) => {
        this.isLoadingMoreData = false;
      }
    );
  }

  @HostListener('window:scroll', ['$event'])
  onWindowScroll() {
    if (
      window.innerHeight + window.scrollY + 250 >= document.body.scrollHeight &&
      !this.disableLoadMoreButton &&
      !this.isLoadingMoreData &&
      this.skipToken != '' &&
      this.query == ''
    ) {
      this.loadMore();
    }
  }

  buildMap(obj: any) {
    let map = new Map();
    Object.keys(obj).forEach((key) => {
      map.set(key, obj[key]);
    });
    return map;
  }

  setItalic(index: number){
    let element = document.getElementById("object-id-" + index);
    element!.style.fontStyle = "italic";
    for(let i = 0; i<this.users.length; i++){
      if(i != index){
        let elementWithoutItalicStyle = document.getElementById("object-id-" + i);
        elementWithoutItalicStyle!.style.fontStyle = "normal";
      }else{
        let element = document.getElementById("object-id-" + index);
        element!.style.fontStyle = "italic";
      }
    }
  }
}
